import React, { useEffect } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import stepFiveVector from '../../../../../common/assets/step_five_vector.png';
import stepFiveVectorDarkOptimal from '../../../../../common/assets/step_five_vector_dark_optimal.png';
import stepFiveVectorLightOptimal from '../../../../../common/assets/step_five_vector_light_optimal.png';
import { Layout } from '../../../../../common/ui/wizard/layout/index.tsx';
import { messages as commonMessages } from '../../../../../common/ui/wizard/layout/messages.tsx';
import { useGlobalChangeboarding } from '../../../../../controllers/store/global/index.tsx';
import { useOgConsolidationUserState } from '../../../../../controllers/use-og-consolidation-user-state/index.tsx';
import { Content } from './content/index.tsx';
import type { StepFiveProps } from './types.tsx';

export const StepFive = ({ onClose, isFirstUnlocked = true }: StepFiveProps) => {
	const { formatMessage } = useIntl();
	const { openTransitionHomeModal, isTransitionHomeLoading, activateTransitionHome } =
		useGlobalChangeboarding();
	const isAdmin = useIsAdmin();
	const isSiteAdmin = useIsSiteAdmin();
	const { update } = useOgConsolidationUserState();

	useEffect(() => {
		// Immediately update user state
		update({
			isWelcomeModalDisplayed: true,
		}).catch(() => {
			// If an error occurs, close the modal
			onClose();
		});
		// Here we don't include onClose as it is probably an inlined function
		// with a state update. It is totally fine to call the initial function
		// passed in when the update is first called even though it is now stale.
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [update]);

	const primaryActionButton = (
		<LoadingButton
			onClick={() => {
				openTransitionHomeModal().then(() => {
					activateTransitionHome();
					onClose();
				});
			}}
			appearance="primary"
			isLoading={isTransitionHomeLoading}
		>
			{formatMessage(commonMessages.openShiftingGuideButtonNonFinal)}
		</LoadingButton>
	);

	const secondaryActionButton = (
		<Button
			onClick={(e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'closeTransitionWizardInUnlockStep');
				onClose();
				activateTransitionHome();
			}}
			appearance="subtle"
		>
			{formatMessage(commonMessages.remindMeLaterButtonNonFinal)}
		</Button>
	);

	return (
		<Layout
			vector={{
				light: isFirstUnlocked ? stepFiveVector : stepFiveVectorLightOptimal,
				dark: isFirstUnlocked ? stepFiveVector : stepFiveVectorDarkOptimal,
			}}
			currentStep={5}
			actionButtons={{
				primary: primaryActionButton,
				secondary: secondaryActionButton,
			}}
			onClose={onClose}
		>
			<Content isFirstUnlocked={isFirstUnlocked} isAdmin={isAdmin || isSiteAdmin} />
		</Layout>
	);
};
